/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useContext, useEffect } from "react";
import { Button, TextField, Autocomplete } from "@mui/material";
import GenericModal from "../../Modal/GenericModal";
import { Vocabulary } from "../../Utils/Vocabulary";
import ClientDropdown, { ClientData } from "../Generic/ClientDropdown";
import VehicleSearchModal from "../Generic/VehicleSearchModal";
import { clientDocType } from "../../Utils/autocompleteEnum";
import { CacheContext } from "../../Context/cacheContext";
import { postData } from "../../Services/postData";
import { urlEnum } from "../../Utils/urlEnum";
import { toast } from "react-toastify";
import PreviewPDF from "../Generic/PDFViewer";

type AddDocumentModalProps = {
  open: boolean;
  onClose: () => void;
  onGenerate?: (templateData: any) => void;
};

export default function AddDocumentModal({
  open,
  onClose,
  onGenerate,
}: AddDocumentModalProps) {
  const [generateData, setGenerateData] = useState({
    name: "",
    type: "",
    description: "",
    content: "",
    tip_fisier: null as any,
    // GDPR specific fields
    companyName: "",
    dpo: "",
    // Antecontract specific fields
    contractTerms: "",
    paymentTerms: "",
    vehicleId: "",
    vehicleName: "",
    VIN: "",
    firma: null as any,
  });

  const [openVehicleSearch, setOpenVehicleSearch] = useState(false);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);

  const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
  const cacheContext = useContext(CacheContext);

  useEffect(() => {
    // Check if company data is loaded in cache
    if (cacheContext?.cache?.company && cacheContext.cache.company.length > 0) {
      setCompaniesLoaded(true);
    }
  }, [cacheContext?.cache?.company]);

  const handleGenerateInputChange = (field: string, value: any) => {
    setGenerateData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectVehicle = (vehicle: any) => {
    handleGenerateInputChange("vehicleId", vehicle.id);
    handleGenerateInputChange("vehicleName", `${vehicle.brand_r?.nume || ""} ${vehicle.model_r?.nume || ""}`.trim());
    handleGenerateInputChange("VIN", vehicle.vin || "");
    setOpenVehicleSearch(false);
  };

  const handleSubmit = async () => {
    if (!selectedClient || !generateData.tip_fisier || !generateData.firma) return;

    try {
      let res;
      
      if (generateData.tip_fisier.nume === "GDPR") {
        // Generate GDPR document
        res = await postData(
          `${urlEnum.templates}/generateGDPR/${selectedClient.id}/${generateData.firma.id}`
        );
      } else if (generateData.tip_fisier.nume === "Antecontract") {
        // Generate Antecontract document
        if (!generateData.vehicleId) {
          toast.error("Please select a vehicle for Antecontract");
          return;
        }
        res = await postData(
          `${urlEnum.templates}/generateBeforeContract/${selectedClient.id}/${generateData.firma.id}/${generateData.vehicleId}`
        );
      } else {
        // For other document types, use the onGenerate callback if provided
        if (onGenerate) {
          onGenerate({
            ...generateData,
            clientId: selectedClient.id,
            mode: "generate",
          });
        }
        handleClose();
        return;
      }

      // Handle the response and show preview
      if (res?.data?.client) {
        const fileName = res.data.client.filePath ? 
          res.data.client.filePath.split("/").pop() : 
          res.data.client.name?.split("/").pop();
        
        if (fileName) {
          toast.success(`${Vocabulary.docGenerated} ${fileName}`);
          
          const file = `${urlEnum.templates}/getDoc/${fileName}`;
          setPreviewData({
            id: selectedClient.id,
            file: file,
            fileName: fileName,
            email: selectedClient.email,
            filePath: res.data.client.filePath || res.data.client.name,
            isSignature: selectedClient.isSignature,
          });
          setOpenPreviewModal(true);
        }
      }
    } catch (error) {
      console.error("Error generating document:", error);
      toast.error("Error generating document");
    }
  };

  const handleClose = () => {
    setGenerateData({
      name: "",
      type: "",
      description: "",
      content: "",
      tip_fisier: null,
      companyName: "",
      dpo: "",
      contractTerms: "",
      paymentTerms: "",
      vehicleId: "",
      vehicleName: "",
      VIN: "",
      firma: null,
    });
    setOpenVehicleSearch(false);
    setOpenPreviewModal(false);
    setPreviewData(null);
    setSelectedClient(null);
    onClose();
  };

  const getGenerateValidation = () => {
    const baseValid = generateData.tip_fisier && selectedClient;
    if (!baseValid) return false;

    // Conditional validation based on document type
    switch (generateData.tip_fisier?.nume) {
      case "GDPR":
        return generateData.firma !== null; // Require company selection
      case "Antecontract":
        return generateData.firma !== null && generateData.vehicleId !== ""; // Require company and vehicle
      default:
        return generateData.content.trim() !== "";
    }
  };

  const isFormValid = getGenerateValidation();

  const modalButtons = (
    <div style={{ display: "flex", gap: "10px" }}>
      <Button variant="outlined" color="secondary" onClick={handleClose}>
        {Vocabulary.cancel}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={!isFormValid}
      >
        {Vocabulary.generateDocument}
      </Button>
    </div>
  );

  return (
    <GenericModal
      open={open}
      onClose={handleClose}
      title={Vocabulary.generateDocument}
      buttons={modalButtons}
      maxWidth="md"
    >
      <div style={{ padding: "20px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            <ClientDropdown
              value={selectedClient}
              onChange={setSelectedClient}
              label={Vocabulary.client}
              fullWidth
              required
            />

            <Autocomplete
              freeSolo={false}
              size="small"
              disableClearable
              disablePortal
              getOptionLabel={(option) => option?.nume || ""}
              isOptionEqualToValue={(option, value) =>
                option?.nume === value?.nume
              }
              value={generateData.tip_fisier}
              onChange={(_, value) =>
                handleGenerateInputChange("tip_fisier", value)
              }
              options={clientDocType}
              renderInput={(params) => (
                <TextField {...params} label="Tip Fisier" required />
              )}
            />

            <Autocomplete
              freeSolo={false}
              style={{ width: "100%" }}
              size="small"
              disableClearable
              disablePortal
              value={generateData.firma}
              onChange={(_, value) => handleGenerateInputChange("firma", value)}
              getOptionLabel={(option: any) => option?.nume_firma || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              options={cacheContext?.cache?.company || []}
              loading={!companiesLoaded}
              renderInput={(params) => (
                <TextField {...params} label="Firma" required />
              )}
            />

            {generateData.tip_fisier?.nume === "Antecontract" && (
              <div
                onClick={() => setOpenVehicleSearch(true)}
                style={{
                  marginLeft: 4,
                  color: "#1976d2",
                  fontSize: 17,
                  cursor: "pointer",
                  textAlign: "center",
                  padding: "10px",
                  border: "1px dashed #1976d2",
                  borderRadius: "5px",
                }}
              >
                {generateData.vehicleId
                  ? `${generateData.vehicleName} (${generateData.VIN})`
                  : Vocabulary.associateWithVehicle}
              </div>
            )}
          </div>
        </div>
      </div>

      <VehicleSearchModal
        open={openVehicleSearch}
        onClose={() => setOpenVehicleSearch(false)}
        onSelectVehicle={handleSelectVehicle}
      />
      
      {previewData && (
        <PreviewPDF
          open={openPreviewModal}
          title={previewData.fileName || Vocabulary.generateDocument}
          onClose={() => {
            setOpenPreviewModal(false);
            setPreviewData(null);
            handleClose(); // Close the main modal after preview
          }}
          signature={true}
          isSign={previewData.isSignature === "1" ? false : true}
          url={previewData.file}
          resetClientFilePathForGDPR={() => {
            setOpenPreviewModal(false);
            setPreviewData(null);
            handleClose();
          }}
        />
      )}
    </GenericModal>
  );
}
